import * as React from "react"
import { createFileRoute } from "@tanstack/react-router"
import { LogOut } from "lucide-react"

import { CoachPicker } from "@/components/coach-picker"
import { MicrophonePicker } from "@/components/microphone-picker"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PageHeader } from "@/components/page-header"
import { ThemeChoice } from "@/components/theme-toggle"
import { cn } from "@/lib/utils"
import { SHOW_IDD_TIME } from "@/lib/feature-flags"
import { formatDurationLong } from "@/lib/idd/active-time"
import {
  useCoachStyles,
  useDeleteLearningEssence,
  useLearningEssence,
  useSessions,
} from "@/lib/queries"
import { useSessionContext } from "@/lib/session"
import { useProfileAutosave, type SaveStatus } from "@/lib/use-profile-autosave"

function ProfilePage() {
  const { user, logout, refresh } = useSessionContext()
  const { data: coachStyles = [] } = useCoachStyles()
  const { data: sessions = [], isLoading: sessionsLoading } = useSessions()
  const [name, setName] = React.useState(user?.name ?? "")
  const [coachId, setCoachId] = React.useState(user?.coachStyleId ?? null)
  const { save, status, clearStatus } = useProfileAutosave(
    { name: user?.name ?? "", coachStyleId: user?.coachStyleId ?? null },
    refresh
  )
  function saveName() {
    setName(name.trim())
    save("name", name)
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <PageHeader
        title="Einstellungen"
        description="Passe dein Konto und dein Training an. Änderungen werden automatisch gespeichert."
      />

      <SettingsSection title="Konto" description="Deine persönlichen Angaben.">
        <div className="space-y-2">
          <Label htmlFor="profile-name">Name</Label>
          <p id="name-description" className="text-sm text-muted-foreground">
            So wirst du in der Anwendung und im Feedback angesprochen.
          </p>
          <Input
            id="profile-name"
            value={name}
            autoComplete="name"
            maxLength={80}
            placeholder="Vorname Nachname"
            className="max-w-[380px]"
            aria-invalid={
              status.name.kind === "invalid" || status.name.kind === "error"
            }
            aria-describedby="name-description name-status"
            onChange={(event) => {
              setName(event.target.value)
              clearStatus("name")
            }}
            onBlur={saveName}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                event.preventDefault()
                event.currentTarget.blur()
              }
            }}
          />
          <SaveFeedback
            id="name-status"
            status={status.name}
            retry={saveName}
          />
        </div>
        <dl className="max-w-lg divide-y divide-[var(--rule)]">
          <ProfileDetail label="E-Mail" value={user?.email ?? "–"} />
          <ProfileDetail label="Rolle" value={user?.roleLabel ?? "–"} />
          {SHOW_IDD_TIME && (
            <ProfileDetail
              label="IDD-Zeit"
              value={
                sessionsLoading
                  ? "–"
                  : formatDurationLong(
                      sessions.reduce(
                        (sum, session) => sum + session.activeSeconds,
                        0
                      )
                    )
              }
            />
          )}
        </dl>
      </SettingsSection>

      <SettingsSection
        title="Training"
        description="Dein Coach und die Technik für deine Gespräche."
      >
        <div className="space-y-3">
          <CoachPicker
            coaches={coachStyles.filter((style) => style.status === "active")}
            value={coachId}
            onChange={(id) => {
              setCoachId(id)
              save("coachStyleId", id)
            }}
          />
          <SaveFeedback
            id="coach-status"
            status={status.coachStyleId}
            retry={() => {
              if (coachId) save("coachStyleId", coachId)
            }}
          />
        </div>
        <div className="space-y-3 border-t border-[var(--rule)] pt-6">
          <h3 className="text-sm font-medium">Mikrofon</h3>
          <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
            Für Live-Gespräche und zum Diktieren. Gilt auf diesem Gerät und wird
            beim nächsten Gespräch verwendet.
          </p>
          <MicrophonePicker />
        </div>
      </SettingsSection>

      <SettingsSection
        title="Darstellung"
        description="Wähle das Erscheinungsbild auf diesem Gerät."
      >
        <div className="space-y-3">
          <ThemeChoice />
          <p className="text-sm text-muted-foreground">
            Mit „System“ folgt die Anwendung der Einstellung deines Rechners.
          </p>
        </div>
      </SettingsSection>

      {(user?.role === "advisor" || user?.role === "platform") && (
        <LearningEssenceSettings />
      )}

      <footer className="space-y-6 border-t border-[var(--rule)] pt-6 pb-4">
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          onClick={() => void logout()}
        >
          <LogOut className="h-4 w-4" />
          Abmelden
        </Button>
      </footer>
    </div>
  )
}

/**
 * Deleting the essence is the advisor's lever over their own record: what the
 * onboarding captured about them is theirs to keep or remove. The conversation
 * itself stays in the chat rail and is deletable there, independently.
 */
function LearningEssenceSettings() {
  const { data: essence } = useLearningEssence()
  const remove = useDeleteLearningEssence()
  // No row, nothing to delete — the section would only raise questions.
  const exists =
    essence && (essence.status !== "open" || essence.threadId !== undefined)
  if (!exists) return null

  return (
    <SettingsSection
      title="Lernentwicklung"
      description="Deine Prioritäten, Stärken und Handlungsfelder aus dem Kennenlernen."
    >
      <div className="space-y-3">
        <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
          Das Löschen entfernt die Essenz und die erfassten Antworten dauerhaft.
          Das Kennenlerngespräch selbst bleibt in deinen Gesprächen und lässt
          sich dort separat löschen. Du kannst das Kennenlernen danach jederzeit
          neu führen.
        </p>
        <Button
          type="button"
          variant="outline"
          className="text-destructive"
          disabled={remove.isPending}
          onClick={() => remove.mutate({})}
        >
          Lernentwicklungs-Essenz löschen
        </Button>
      </div>
    </SettingsSection>
  )
}

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  const id = React.useId()
  return (
    <section
      aria-labelledby={id}
      className="space-y-6 rounded-xl border border-[var(--rule)] bg-card p-5 sm:p-6"
    >
      <div>
        <h2
          id={id}
          className="font-display text-[22px] font-medium tracking-tight"
        >
          {title}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  )
}

function SaveFeedback({
  id,
  status,
  retry,
}: {
  id: string
  status: SaveStatus
  retry: () => void
}) {
  const error = status.kind === "error" || status.kind === "invalid"
  return (
    <div
      id={id}
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        "flex min-h-5 flex-wrap items-center gap-x-3 text-xs",
        error ? "text-destructive" : "text-muted-foreground"
      )}
    >
      <span>
        {status.message ??
          (status.kind === "saving"
            ? "Wird gespeichert …"
            : status.kind === "saved"
              ? "Gespeichert"
              : "")}
      </span>
      {status.kind === "error" && (
        <button
          type="button"
          onClick={retry}
          className="rounded-sm underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-ring"
        >
          Erneut versuchen
        </button>
      )}
    </div>
  )
}

function ProfileDetail({
  label,
  value,
  mono = false,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "min-w-0 text-right text-[13.5px] text-foreground/85",
          mono && "font-mono text-[12.5px] break-all"
        )}
      >
        {value}
      </dd>
    </div>
  )
}

export const Route = createFileRoute("/_app/profile")({
  component: ProfilePage,
})
